import { useState, useEffect } from 'react';
import { settings, ncf, microsoftIntegration } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Settings as SettingsIcon, Hash, Save, RefreshCw, FileText, AlertTriangle,
  Building2, CreditCard, Users, Landmark, Link2, Calendar, Mail, BarChart3, CheckCircle2, XCircle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const NCF_ICONS = {
  '01': CreditCard,
  '02': Users,
  '14': Building2,
  '15': Landmark,
};

function NCFSequenceCard({ sequence, ncfInfo, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [newSequence, setNewSequence] = useState(sequence.current_sequence);
  const [saving, setSaving] = useState(false);

  const Icon = NCF_ICONS[sequence.code] || FileText;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(sequence.ncf_type, parseInt(newSequence));
      setEditing(false);
      toast.success('Secuencia actualizada');
    } catch (err) {
      toast.error('Error al actualizar secuencia');
    } finally {
      setSaving(false);
    }
  };

  const nextNCF = `B${sequence.code}${String(sequence.current_sequence + 1).padStart(8, '0')}`;

  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Icon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{ncfInfo?.name || sequence.name}</h3>
              <p className="text-sm text-muted-foreground">
                {ncfInfo?.description || 'Comprobante fiscal'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline">Código: {sequence.code}</Badge>
                {ncfInfo?.requires_rnc && (
                  <Badge variant="secondary">Requiere RNC</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Próximo NCF:</p>
              <p className="font-mono text-xl font-bold text-primary">{nextNCF}</p>
            </div>
            
            {editing ? (
              <div className="flex items-center gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Secuencia actual</Label>
                  <Input
                    type="number"
                    value={newSequence}
                    onChange={(e) => setNewSequence(e.target.value)}
                    className="w-32 font-mono"
                    min="0"
                    max="99999999"
                  />
                </div>
                <div className="flex gap-1 mt-5">
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Secuencia actual</p>
                <p className="font-mono text-2xl font-bold">{sequence.current_sequence}</p>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setEditing(true)}
                  className="mt-1"
                >
                  Editar
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const [ncfSettings, setNcfSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await settings.getNcfSettings();
      setNcfSettings(res.data);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSequence = async (ncfType, newSequence) => {
    await settings.updateNcfSequence(ncfType, newSequence);
    loadSettings();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Configuración</h1>
        <p className="text-muted-foreground">Ajustes del sistema y secuencias NCF</p>
      </div>

      <Tabs defaultValue="ncf" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ncf" className="gap-2">
            <Hash className="h-4 w-4" />
            Secuencias NCF
          </TabsTrigger>
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="h-4 w-4" />
            Empresa
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Link2 className="h-4 w-4" />
            Integraciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ncf" className="space-y-4">
          {/* Warning */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Importante</p>
                  <p className="text-sm text-amber-700">
                    Las secuencias NCF son controladas por la DGII. Solo modifique estos valores si necesita 
                    sincronizar con un sistema existente o si la DGII le ha asignado nuevas secuencias.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NCF Types Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tipos de NCF - DGII República Dominicana</CardTitle>
              <CardDescription>
                Comprobantes Fiscales según la Dirección General de Impuestos Internos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Requiere RNC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ncfSettings?.ncf_types && Object.entries(ncfSettings.ncf_types).map(([key, info]) => (
                    <TableRow key={key}>
                      <TableCell className="font-mono font-bold">{info.code}</TableCell>
                      <TableCell className="font-medium">{info.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-md">
                        {info.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant={info.requires_rnc ? 'default' : 'secondary'}>
                          {info.requires_rnc ? 'Sí' : 'No'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Sequence Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {ncfSettings?.sequences?.map((seq) => (
              <NCFSequenceCard
                key={seq.ncf_type}
                sequence={seq}
                ncfInfo={ncfSettings.ncf_types?.[seq.ncf_type]}
                onUpdate={handleUpdateSequence}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="company" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Datos de la Empresa</CardTitle>
              <CardDescription>
                Información que aparece en facturas y documentos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Empresa</Label>
                  <Input defaultValue="TRANSTAR SRL" disabled />
                </div>
                <div className="space-y-2">
                  <Label>RNC</Label>
                  <Input defaultValue="000-00000-0" placeholder="Ingrese su RNC" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input defaultValue="Santo Domingo, República Dominicana" placeholder="Dirección completa" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input placeholder="809-000-0000" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input placeholder="contacto@empresa.com" />
                </div>
              </div>
              <div className="pt-4">
                <Button disabled>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  * Esta funcionalidad estará disponible próximamente
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <MicrosoftIntegrationCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MicrosoftIntegrationCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [config, setConfig] = useState({
    client_id: '',
    client_secret: '',
    tenant_id: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await microsoftIntegration.getStatus();
      setStatus(res.data);
    } catch (err) {
      console.error('Error loading Microsoft status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = async () => {
    if (!config.client_id || !config.tenant_id) {
      toast.error('Client ID y Tenant ID son requeridos');
      return;
    }

    setConfiguring(true);
    try {
      const res = await microsoftIntegration.configure(config);
      if (res.data.authorization_url) {
        toast.success('Configuración guardada. Redirigiendo a Microsoft...');
        window.open(res.data.authorization_url, '_blank');
      }
    } catch (err) {
      toast.error('Error al configurar integración');
    } finally {
      setConfiguring(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Microsoft Integration Status */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-sky-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <svg className="h-6 w-6" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="#f35325" d="M1 1h10v10H1z"/>
                <path fill="#81bc06" d="M12 1h10v10H12z"/>
                <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                <path fill="#ffba08" d="M12 12h10v10H12z"/>
              </svg>
            </div>
            <div>
              <CardTitle className="text-lg">Microsoft 365 Integration</CardTitle>
              <CardDescription>
                Conecta tu cuenta de Microsoft para sincronizar calendario y métricas
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Features */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-blue-800">Funcionalidades Disponibles:</p>
              <div className="space-y-2">
                {[
                  { icon: Calendar, name: 'Sincronización de Calendario', available: status?.features_available?.calendar_sync },
                  { icon: Mail, name: 'Integración de Email', available: status?.features_available?.email_integration },
                  { icon: Users, name: 'Microsoft Teams', available: status?.features_available?.teams_integration },
                  { icon: BarChart3, name: 'Métricas de Productividad', available: status?.features_available?.productivity_insights },
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {feature.available ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                    <feature.icon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics Available */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-blue-800">Métricas que Podrás Medir:</p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                {status?.available_metrics_with_microsoft?.slice(0, 6).map((metric, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                    {metric}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {!status?.configured && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 font-medium">
                ⚠️ Integración no configurada
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Necesitas crear una aplicación en Azure Portal para conectar Microsoft 365.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Instrucciones de Configuración
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {Object.entries(status?.setup_instructions || {}).filter(([key]) => key.startsWith('step')).map(([key, value]) => (
              <div key={key} className="flex items-start gap-3">
                <Badge variant="outline" className="mt-0.5">{key.replace('step_', '')}</Badge>
                <p className="text-sm">{value}</p>
              </div>
            ))}
          </div>

          <div className="pt-4">
            <a 
              href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir Azure Portal - App Registrations
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Form (Admin Only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Configurar Credenciales
            </CardTitle>
            <CardDescription>
              Ingresa las credenciales de tu aplicación Azure AD
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client ID (Application ID)</Label>
                <Input
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={config.client_id}
                  onChange={(e) => setConfig({...config, client_id: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Tenant ID (Directory ID)</Label>
                <Input
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={config.tenant_id}
                  onChange={(e) => setConfig({...config, tenant_id: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Client Secret</Label>
              <Input
                type="password"
                placeholder="Tu Client Secret de Azure"
                value={config.client_secret}
                onChange={(e) => setConfig({...config, client_secret: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                El Client Secret se genera en Azure Portal &gt; App &gt; Certificates & secrets
              </p>
            </div>
            <Button 
              onClick={handleConfigure} 
              disabled={configuring}
              className="w-full md:w-auto"
            >
              {configuring ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Configurando...
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  Conectar con Microsoft
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
